<!--
 * @Description: 移动端工具选择器下拉组件
 * @Date: 2026-01-08
-->
<template>
    <div class="mobile-tool-selector lg:hidden" v-click-outside="closeDropdown">
        <!-- 触发器 -->
        <div class="selector-trigger" @click="toggleDropdown">
            <!-- 根据当前工具类型显示分类图标 -->
            <svg v-if="currentToolCategory === 'image'" class="trigger-icon-svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M5 21q-.825 0-1.413-.587T3 19V5q0-.825.588-1.413T5 3h14q.825 0 1.413.588T21 5v14q0 .825-.587 1.413T19 21H5Zm0-2h14V5H5v14Zm1-2h12l-3.75-5-3 4L9 13l-3 4Zm2-7q.625 0 1.063-.437T9.5 8.5q0-.625-.437-1.063T8 7q-.625 0-1.063.438T6.5 8.5q0 .625.438 1.063T8 10Z"/>
            </svg>
            <svg v-else-if="currentToolCategory === 'video'" class="trigger-icon-svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M4 20q-.825 0-1.413-.587T2 18V6q0-.825.588-1.413T4 4h12q.825 0 1.413.588T18 6v4.5l4-4v11l-4-4V18q0 .825-.587 1.413T16 20H4Zm0-2h12V6H4v12Zm0 0V6v12Z"/>
            </svg>
            <svg v-else class="trigger-icon-svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6Zm-2 16c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Z"/>
            </svg>
            <span class="trigger-text">{{ currentToolName }}</span>
            <span class="trigger-arrow" :class="{ 'is-open': isDropdownOpen }">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </span>
        </div>

        <!-- 下拉列表 -->
        <Transition name="dropdown">
            <div v-if="isDropdownOpen" class="selector-dropdown">
                <!-- AI 图像分类 -->
                <div class="dropdown-group">
                    <div class="group-header">
                        <svg class="group-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M5 21q-.825 0-1.413-.587T3 19V5q0-.825.588-1.413T5 3h14q.825 0 1.413.588T21 5v14q0 .825-.587 1.413T19 21H5Zm0-2h14V5H5v14Zm1-2h12l-3.75-5-3 4L9 13l-3 4Zm2-7q.625 0 1.063-.437T9.5 8.5q0-.625-.437-1.063T8 7q-.625 0-1.063.438T6.5 8.5q0 .625.438 1.063T8 10Z"/>
                        </svg>
                        <span class="group-title">{{ t('menus.AIImages') }}</span>
                    </div>
                    <div class="group-items">
                        <div
                            v-for="item in imageTools"
                            :key="item.path"
                            class="dropdown-item"
                            :class="{ 'is-selected': isCurrentPath(item.path) }"
                            @click="handleSelect(item.path)"
                        >
                            <span class="item-text">{{ item.name }}</span>
                            <svg
                                v-if="isCurrentPath(item.path)"
                                class="item-check-svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <path d="M5 12l5 5L20 7" stroke="#99f768" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                    </div>
                </div>

                <!-- AI 视频分类 -->
                <div class="dropdown-group">
                    <div class="group-header">
                        <svg class="group-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M4 20q-.825 0-1.413-.587T2 18V6q0-.825.588-1.413T4 4h12q.825 0 1.413.588T18 6v4.5l4-4v11l-4-4V18q0 .825-.587 1.413T16 20H4Zm0-2h12V6H4v12Zm0 0V6v12Z"/>
                        </svg>
                        <span class="group-title">{{ t('menus.AIVideo') }}</span>
                    </div>
                    <div class="group-items">
                        <div
                            v-for="item in videoTools"
                            :key="item.path"
                            class="dropdown-item"
                            :class="{ 'is-selected': isCurrentPath(item.path) }"
                            @click="handleSelect(item.path)"
                        >
                            <span class="item-text">{{ item.name }}</span>
                            <svg
                                v-if="isCurrentPath(item.path)"
                                class="item-check-svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <path d="M5 12l5 5L20 7" stroke="#99f768" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                    </div>
                </div>

                <!-- 音乐分类 -->
                <div class="dropdown-group">
                    <div class="group-header">
                        <svg class="group-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6Zm-2 16c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Z"/>
                        </svg>
                        <span class="group-title">{{ t('menus.music') }}</span>
                    </div>
                    <div class="group-items">
                        <div
                            v-for="item in musicTools"
                            :key="item.path"
                            class="dropdown-item"
                            :class="{ 'is-selected': isCurrentPath(item.path) }"
                            @click="handleSelect(item.path)"
                        >
                            <span class="item-text">{{ item.name }}</span>
                            <svg
                                v-if="isCurrentPath(item.path)"
                                class="item-check-svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <path d="M5 12l5 5L20 7" stroke="#99f768" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute } from '#imports'
import { useI18n } from '~/composables/useI18n'
import { useToolsHooks } from '@/utils/useHooks'
import { deleteLangPrefix } from '~/utils/common'
import { useAppStore } from '~/stores/app'

const route = useRoute()
const appStore = useAppStore()
const { t } = useI18n()
const { handleMenuLinkClick } = useToolsHooks()

// 下拉框状态
const isDropdownOpen = ref(false)

// 图片工具列表
const imageTools = computed(() => {
    const items = [
        {
            path: '/text-to-image',
            name: t('menus.textToImage'),
            icon: 'i-text-to-image-icon',
        },
        {
            path: '/image-to-image',
            name: t('menus.imageToImage'),
            icon: 'i-image-to-image-icon',
        },
        {
            path: '/character-to-image',
            name: t('characterToImg.characterToImage'),
            icon: 'i-character-to-image-icon',
        },
        {
            path: '/ai-tools',
            name: t('menus.AITools'),
            icon: 'i-ai-tool-icon',
        },
        {
            path: '/photo-effects',
            name: t('menus.photoEffects'),
            icon: 'i-ai-image-icon',
        },
    ]

    if (!appStore.hideCharacterEntries) {
        return items
    }

    return items.filter(item => item.path !== '/character-to-image')
})

// 视频工具列表
const videoTools = computed(() => {
    const items = [
        {
            path: '/text-to-video',
            name: t('menus.textToVideo'),
            icon: 'i-text-to-video-icon',
        },
        {
            path: '/image-to-video',
            name: t('menus.imageToVideo'),
            icon: 'i-img-to-video-icon',
        },
        {
            path: '/character-to-video',
            name: t('characterToImg.characterToVideo'),
            icon: 'i-character-to-image-icon',
        },
        {
            path: '/video-to-video',
            name: t('menus.videoToVideo'),
            icon: 'i-video-to-video-icon',
        },
        {
            path: '/ai-tools?tab=video',
            name: t('menus.AITools'),
            icon: 'i-ai-tool-icon',
        },
        {
            path: '/video-effects',
            name: t('menus.videoEffects'),
            icon: 'i-video-effects-icon',
        },
    ]

    if (!appStore.hideCharacterEntries) {
        return items
    }

    return items.filter(item => item.path !== '/character-to-video')
})

// 音乐工具列表
const musicTools = computed(() => [
    {
        path: '/text-to-music',
        name: t('menus.generateMusic'),
    },
])

// 图片编辑工具路径列表（属于 AI Tools 分类）
const imageEditToolPaths = [
    '/ai-image-resizer',
    '/image-text-translate',
    '/ai-background-changer',
    '/image-upscaler',
    // '/photo-face-swap',
    '/remove-text-from-image',
    '/photo-restoration',
    '/ai-image-extender',
    '/ai-image-background-remover',
]

// 视频编辑工具路径列表（属于 AI Tools 分类）
const videoEditToolPaths = [
    '/remove-video-watermark',
    // '/video-face-swap',
    '/video-lip-sync',
    '/translate-video-subtitle',
    '/video-to-anime',
    '/video-upscaler',
    '/remove-video-subtitles',
    '/video-background-remover',
    '/ai-video-extender',
    '/video-motion-imitation',
]

// 所有工具列表（用于查找当前工具）
const allTools = computed(() => {
    // 合并所有工具，去掉重复的 ai-tools
    const tools = [
        ...imageTools.value,
        ...videoTools.value.filter(t => !t.path.startsWith('/ai-tools')),
        ...musicTools.value,
    ]
    return tools
})

// 当前路径
const currentPath = computed(() => deleteLangPrefix(route.path) || '/')

// 判断是否为当前路径
const isCurrentPath = (path: string) => {
    return currentPath.value === path || currentPath.value.startsWith(path + '/')
}

// 判断当前路径是否为图片编辑工具
const isImageEditTool = computed(() => {
    return imageEditToolPaths.some(p => currentPath.value === p || currentPath.value.startsWith(p + '/'))
})

// 判断当前路径是否为视频编辑工具
const isVideoEditTool = computed(() => {
    return videoEditToolPaths.some(p => currentPath.value === p || currentPath.value.startsWith(p + '/'))
})

// 当前选中的工具
const currentTool = computed(() => {
    // 如果是图片编辑工具，返回图片分类的 AI Tools
    if (isImageEditTool.value) {
        return imageTools.value.find(t => t.path === '/ai-tools')
    }
    // 如果是视频编辑工具，返回视频分类的 AI Tools
    if (isVideoEditTool.value) {
        return videoTools.value.find(t => t.path.startsWith('/ai-tools'))
    }
    return allTools.value.find(tool => isCurrentPath(tool.path)) || imageTools.value[0]
})

// 当前工具所属分类
const currentToolCategory = computed(() => {
    // 如果是图片编辑工具，归类为图片
    if (isImageEditTool.value) return 'image'
    // 如果是视频编辑工具，归类为视频
    if (isVideoEditTool.value) return 'video'
    
    const isImageTool = imageTools.value.some(tool => isCurrentPath(tool.path))
    const isVideoTool = videoTools.value.some(tool => isCurrentPath(tool.path))
    if (isImageTool) return 'image'
    if (isVideoTool) return 'video'
    return 'music'
})

// 当前工具名称
const currentToolName = computed(() => currentTool.value?.name || t('menus.textToImage'))

// 切换下拉框
const toggleDropdown = () => {
    isDropdownOpen.value = !isDropdownOpen.value
}

// 关闭下拉框
const closeDropdown = () => {
    isDropdownOpen.value = false
}

// 选择工具
const handleSelect = (path: string) => {
    closeDropdown()
    handleMenuLinkClick(path)
}

// 点击外部关闭指令
interface ClickOutsideElement extends HTMLElement {
    clickOutsideEvent?: (event: Event) => void
}

const vClickOutside = {
    mounted(el: ClickOutsideElement, binding: any) {
        el.clickOutsideEvent = (event: Event) => {
            if (!(el === event.target || el.contains(event.target as Node))) {
                binding.value()
            }
        }
        document.addEventListener('click', el.clickOutsideEvent)
    },
    unmounted(el: ClickOutsideElement) {
        if (el.clickOutsideEvent) {
            document.removeEventListener('click', el.clickOutsideEvent)
        }
    },
}
</script>

<style scoped lang="less">
.mobile-tool-selector {
    position: relative;
    display: inline-flex;
    
    // PC端隐藏 (lg = 1024px)
    @media (min-width: 1024px) {
        display: none !important;
    }
}

.selector-trigger {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    background: rgba(38, 38, 42, 0.9);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 20px;
    cursor: pointer;
    user-select: none;
    transition: all 0.2s;

    &:active {
        opacity: 0.8;
        transform: scale(0.98);
    }

    .trigger-icon-svg {
        width: 16px;
        height: 16px;
        color: #99f768;
        flex-shrink: 0;
    }

    .trigger-text {
        color: #ffffff;
        font-size: 14px;
        font-weight: 500;
        white-space: nowrap;
    }

    .trigger-arrow {
        color: rgba(255, 255, 255, 0.6);
        display: flex;
        align-items: center;
        transition: transform 0.3s ease;

        &.is-open {
            transform: rotate(180deg);
        }
    }
}

.selector-dropdown {
    position: fixed;
    top: 72px; // ToolProjectTab 的 64px 高度 + 8px 间距
    left: 50%;
    transform: translateX(-50%);
    min-width: 200px;
    max-width: calc(100vw - 32px);  // 留出左右边距
    max-height: 70vh;
    overflow-y: auto;
    background: #1a1a1e;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 8px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
    z-index: 1000;
    
    // 隐藏滚动条但保持滚动功能
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */
    
    &::-webkit-scrollbar {
        display: none; /* Chrome/Safari */
    }
}

.dropdown-group {
    &:not(:last-child) {
        margin-bottom: 8px;
        padding-bottom: 8px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    }
}

.group-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;

    .group-icon {
        width: 16px;
        height: 16px;
        color: #99f768;
    }

    .group-title {
        color: rgba(255, 255, 255, 0.5);
        font-size: 12px;
        font-weight: 500;
    }
}

.group-items {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.dropdown-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 14px;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s;

    .item-text {
        flex: 1;
        color: #ffffff;
        font-size: 14px;
        font-weight: 400;
    }

    .item-check-svg {
        width: 18px;
        height: 18px;
        flex-shrink: 0;
    }

    &:hover {
        background: rgba(255, 255, 255, 0.08);
    }

    &.is-selected {
        background: rgba(153, 247, 104, 0.1);
        
        .item-text {
            color: #99f768;
            font-weight: 500;
        }
    }
}

// 下拉动画
.dropdown-enter-active {
    animation: dropdown-in 0.25s ease-out;
}

.dropdown-leave-active {
    animation: dropdown-out 0.2s ease-in;
}

@keyframes dropdown-in {
    from {
        opacity: 0;
        transform: translateX(-50%) translateY(-8px);
    }
    to {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
    }
}

@keyframes dropdown-out {
    from {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
    }
    to {
        opacity: 0;
        transform: translateX(-50%) translateY(-8px);
    }
}
</style>
